import { cn } from "@fatbees/ui-utils";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";
import { ForwardedRef, forwardRef, ReactNode } from "react";

export interface SelectProps extends React.ComponentProps<
  typeof SelectPrimitive.Root
> {}

export function Select({ ...props }: SelectProps) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

export function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

export const SelectValue = forwardRef(function SelectValue(
  { ...props }: React.ComponentProps<typeof SelectPrimitive.Value>,
  ref: ForwardedRef<HTMLSpanElement>,
) {
  return (
    <SelectPrimitive.Value data-slot="select-value" ref={ref} {...props} />
  );
});

export interface SelectTriggerProps extends React.ComponentProps<
  typeof SelectPrimitive.Trigger
> {
  size?: "sm" | "default";
  showIcon?: boolean;
}

export const SelectTrigger = forwardRef(function SelectTrigger(
  {
    className,
    size = "default",
    showIcon = true,
    children,
    ...props
  }: SelectTriggerProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      {showIcon && (
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  );
});

export interface SelectContentProps extends React.ComponentProps<
  typeof SelectPrimitive.Content
> {}

export const SelectContent = forwardRef(function SelectContent(
  { className, children, position = "popper", ...props }: SelectContentProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        ref={ref}
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});

export interface SelectLabelProps extends React.ComponentProps<
  typeof SelectPrimitive.Label
> {}

export const SelectLabel = forwardRef(function SelectLabel(
  { className, ...props }: SelectLabelProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      ref={ref}
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
});

export interface SelectItemProps extends React.ComponentProps<
  typeof SelectPrimitive.Item
> {}

export const SelectItem = forwardRef(function SelectItem(
  { className, children, ...props }: SelectItemProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      ref={ref}
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});

export interface SelectItemContentProps {
  children?: ReactNode;
  details?: ReactNode;
  className?: string;
}

export function SelectItemContent({
  children,
  details,
  className,
}: SelectItemContentProps) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {children}
      {details && (
        <div className="text-xs text-muted-foreground">{details}</div>
      )}
    </div>
  );
}

export interface SelectSeparatorProps extends React.ComponentProps<
  typeof SelectPrimitive.Separator
> {}

export const SelectSeparator = forwardRef(function SelectSeparator(
  { className, ...props }: SelectSeparatorProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      ref={ref}
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
});

export interface SelectScrollUpButtonProps extends React.ComponentProps<
  typeof SelectPrimitive.ScrollUpButton
> {}

export const SelectScrollUpButton = forwardRef(function SelectScrollUpButton(
  { className, ...props }: SelectScrollUpButtonProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      ref={ref}
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
});

export interface SelectScrollDownButtonProps extends React.ComponentProps<
  typeof SelectPrimitive.ScrollDownButton
> {}

export const SelectScrollDownButton = forwardRef(
  function SelectScrollDownButton(
    { className, ...props }: SelectScrollDownButtonProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    return (
      <SelectPrimitive.ScrollDownButton
        data-slot="select-scroll-down-button"
        ref={ref}
        className={cn(
          "flex cursor-default items-center justify-center py-1",
          className,
        )}
        {...props}
      >
        <ChevronDownIcon className="size-4" />
      </SelectPrimitive.ScrollDownButton>
    );
  },
);
